import { Entity } from 'dexie';
import { Attribute, CoyoteWS } from '../../class/Attribute';
import { DEVICES_HAS_BEACON, DEVICES_HAS_CAN } from '../../class/Device';
import { DevicesManagerService } from '../../services/devices-manager.service';
import type { AppDB } from '../db';

export const DEVICE_STATUS = {
  fullyStopped: {
    text: 'A l’arrêt (contact éteint)',
    value: 'fullyStopped',
  },
  stopped: {
    text: 'A l’arrêt (contact allumé)',
    value: 'stopped',
  },
  moving: {
    text: 'En déplacement',
    value: 'moving',
  },
  inactive: {
    text: 'inactive',
    value: 'inactive',
  },
} as const;

class Location extends Entity<AppDB> {
  public uid: number;
  public registration: string;
  public uid_parent: number;
  public id_symbol: number;
  public pda: string; // "Y" | "N"
  public summary: string;
  public available: string; // "Y" | "N"
  public uid_picture: number | null;
  public serial_number: string | null;
  public mileage: number;
  public engine: number;
  public device_type: number;
  public dev_hw_id: string;
  public gps_sat_used: number;
  public speed_max: number;
  public special_flag: number;
  public last_data_timestamp: number;
  public device_log: number;
  public first_day_trip: number;
  public last_trip_stop: number;
  public last_trip_start: number;
  public location_date: number;
  public longitude: number | null;
  public latitude: number | null;
  public speed_current: number | null;
  public input: number | null;
  public course: number | null;
  public csq: number | null;
  public hdop: number | null;
  public entry_state: number | null;
  public status: number;
  public uid_driver: number;
  public driver_name: string;
  public driver_phone: string;
  public battery_level: number;
  public battery_volt: number;
  public battery_last_timestamp: number;
  public address: string;
  public hardware: string;
  public poi: string | null;
  public uid_zoi: number | null;
  public keywords: string;
  public temperature: number | null;
  public locking_request_type: string | null;
  public locking_request_status: string | null;
  public locking_request_created: string | null;
  public locking_request_successed_type: string | null;
  public shipping_code: string;
  public snexternal: string | null;
  public attributes: string | null;
  public normalizeAttributes: Attribute[] | null;
  public uid_pictures: number[] | null;
  public soh: string | null;
  public vin: string | null;

  public fk_asset: number | null;
  public weight: number;

  public icon: number;
  public deviceStatusClass: string;
  public movingStatus: (typeof DEVICE_STATUS)[keyof typeof DEVICE_STATUS] = DEVICE_STATUS.inactive;
  public newStatus: number;
  public status_lock: string;
  public isLocked: boolean;
  public isWaitingCommand: boolean;
  public isPrivacy = this.hasPrivacy();
  public isBlurHdop: boolean;
  public anomalyError = DevicesManagerService.getAnomalyError(this);
  public batteryLevelIcon: string;
  // The argument is conditional because if the device comes from the backend, device.attributes contains a JSON with both attributes and coyoteWS
  // but if the device comes from the storageService, it has already been parsed and the two values (attributes and coyoteWS) are separated.
  // In this case, we get a device object containing a coyoteWS key and an already parsed attributes key.
  public coyoteWS: CoyoteWS;

  public normalize() {
    DevicesManagerService.setStatusDevice(this);
    DevicesManagerService.setStatusLock(this);
    this.registration = this.registration || this.uid.toString();
    this.isPrivacy = this.hasPrivacy();
    this.anomalyError = DevicesManagerService.getAnomalyError(this);
    this.uid_driver = Number(this.uid_driver);
    DevicesManagerService.setAttributes(this);
    DevicesManagerService.setHDOP(this);
    DevicesManagerService.setBatteryLevel(this);
  }

  /**
   * Get if the current state of device is privacy or not
   */
  public hasPrivacy() {
    return (this.special_flag & 2) !== 0;
  }

  public hasCAN() {
    return DEVICES_HAS_CAN.includes(this.hardware);
  }

  get hasBeacon() {
    return this.hardware === DEVICES_HAS_BEACON;
  }

  public attributesJson(): string {
    return JSON.stringify({ attributes: this.attributes });
  }

  public get keywordList() {
    return !!this.keywords && this.keywords !== '' ? this.keywords.split(',').map(Number) : [];
  }
}

export default Location;
